import { FunctionComponent, useEffect } from "react";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";

import { useAddInboxChat } from "../utils/useAddInboxChat";

/**
 * Per-lead "Chat" tab for the Lead Panel (List detail view + Chrome extension).
 *
 * Reuses the full Inbox/Compose drafting experience (conversation history,
 * editable draft cards, "Tell AI" generation, templates/snippets, send) by
 * driving {@link useAddInboxChat} from an explicit `lead` instead of the list
 * slice's `inbox[currentLeadIndex]`. This keeps it self-contained: a single
 * `listPerson` in, no dependency on the Inbox page's selection state.
 *
 * `renderChatlog` already renders the composer editor and the floating "Tell AI"
 * generate panel (a portal); only the send-button row is added below it.
 */
export const LeadChatTab: FunctionComponent<{
	lead: ListPerson;
	/**
	 * Portal target for the floating "Tell AI" panel. The Chrome extension passes
	 * its shadow-root container so the panel renders inside the isolated style
	 * scope instead of the host page. Defaults to `document.body` (web app).
	 */
	portalContainer?: HTMLElement | null;
	/**
	 * Confine the floating "Tell AI" overlay to `portalContainer` instead of the
	 * viewport. The web Lead Panel hosts set this (they pass their positioned panel
	 * element), so the scrim dims only the panel and the card centers inside it. The
	 * extension must not set it — its portal host is a 0×0 shadow-root element.
	 */
	scopeGeneratePanelToPortal?: boolean;
	/**
	 * Render the empty-chat "Tell AI" bar collapsed (a strip that expands on click).
	 * Set when the tab was entered via "Write manually", so the AI panel doesn't
	 * dominate the manual composer.
	 */
	collapsedGenerateBar?: boolean;
	/**
	 * Open the floating "Tell AI" panel as soon as the tab mounts. Set when the tab
	 * was entered from a pinned research fact's "In sequence" chip (PREC-3680), so
	 * the user lands ready to generate instead of having to find the AI entry point.
	 */
	autoOpenGenerateBar?: boolean;
	/**
	 * Called after a successful send. Sending removes the lead from the inbox slice,
	 * so the host should react (the List detail view closes the panel; the extension
	 * returns to the Lead Panel view).
	 */
	onSent?: () => void;
}> = ({
	lead,
	portalContainer,
	scopeGeneratePanelToPortal,
	collapsedGenerateBar,
	autoOpenGenerateBar,
	onSent,
}) => {
	const { renderChatlog, renderSendButtons, openGeneratePanel } = useAddInboxChat({
		prefillDrafts: true,
		lead,
		portalContainer,
		scopeGeneratePanelToPortal,
		panelLayout: true,
		collapsedGenerateBar,
		onSent,
	});

	// Fires once per mount: the hosts mount this tab fresh when switching to the
	// Chat view, so there is no need to track "already opened" — and re-opening a
	// panel the user deliberately closed would be worse than not opening it.
	useEffect(() => {
		if (autoOpenGenerateBar) {
			openGeneratePanel();
		}
	}, []);

	return (
		<div className="flex h-full flex-col" data-testid="lead-chat-tab">
			<div className="min-h-0 flex-1 overflow-hidden">{renderChatlog([], false)}</div>
			<div className="flex shrink-0 justify-end gap-2 border-t border-border-light bg-white px-4 py-3">
				{renderSendButtons()}
			</div>
		</div>
	);
};
